import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Popover,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import {
  Attendance,
  PaginatedResponse,
} from '../../types/AssessmentTable.type';
import { apiGet } from '../../utils/apiClient';
import { rowHoverBg, selectedRowBg } from '../../utils/rowHighlight';
import { Replay } from '@mui/icons-material';
import TableLoader from '../common/TableLoader';

type AttendanceDialogProps = {
  open: boolean;
  handleClose: () => void;
  registrationId: number | null;
  handleUndoAttendance: (id: number) => void;
};

const AttendanceDialog = ({
  open,
  handleClose,
  registrationId,
  handleUndoAttendance,
}: AttendanceDialogProps) => {
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filterDate, setFilterDate] = useState<Dayjs | null>(null);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  const fetchAttendance = async () => {
    if (registrationId) {
      try {
        setLoading(true);
        const response = await apiGet<PaginatedResponse<Attendance>>(
          `/attendance/list/${registrationId}?page=${page + 1}&limit=${limit}&date=${
            filterDate && filterDate.isValid()
              ? filterDate.format('YYYY-MM-DD')
              : ''
          }`,
        );
        setAttendance(response.data);
        setTotalItems(response.metadata.totalItems);
      } catch (error) {
        console.error('Error fetching attendance:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseDialog = () => {
    handleClose();
    setAttendance([]);
    setPage(0);
    setLimit(10);
    setTotalItems(0);
    setFilterDate(null);
  };

  const handleConfirm = (id: number) => {
    handleUndoAttendance(id);
    setAttendance((prev) => prev.filter((att) => att.id !== id));
  };

  useEffect(() => {
    if (open) {
      fetchAttendance();
    }
  }, [open, registrationId, page, limit, filterDate]);

  return (
    <Dialog fullWidth open={open} onClose={handleCloseDialog}>
      <DialogTitle>Check in Information</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            my: 2,
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              label="Filter by Check-in Date"
              value={filterDate}
              onChange={(newDate) => setFilterDate(newDate)}
              slotProps={{
                field: {
                  clearable: true,
                  onClear: () => setFilterDate(null),
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#4da7db' }}>
                <TableCell>
                  <Typography fontWeight="bold" color="#fff">
                    Date
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="#fff">
                    Time
                  </Typography>
                </TableCell>
                {/* <TableCell>
                  <Typography fontWeight="bold" color="#fff">
                    Coach
                  </Typography>
                </TableCell> */}
                <TableCell>
                  <Typography fontWeight="bold" color="#fff">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoader colSpan={3} />
              ) : (
                <>
              {attendance.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    '&:hover': { backgroundColor: rowHoverBg },
                    backgroundColor:
                      highlightedRow === `data_${item.id}`
                        ? selectedRowBg
                        : 'inherit',
                  }}
                  onClick={() => {
                    if (highlightedRow === `data_${item.id}`) {
                      setHighlightedRow(null);
                    } else {
                      setHighlightedRow(`data_${item.id}`);
                    }
                  }}
                >
                  <TableCell>
                    {new Date(item.attendanceDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(item.attendanceDate).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </TableCell>
                  {/* <TableCell>{'---'}</TableCell> */}
                  <TableCell>
                    <ConfirmationPopover
                      attendanceId={item.id}
                      handleConfirm={handleConfirm}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {attendance.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No attendance found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalItems}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const ConfirmationPopover = ({
  attendanceId,
  handleConfirm,
}: {
  attendanceId: number;
  handleConfirm: (id: number) => void;
}) => {
  const [openConfirmation, setOpenConfirmation] =
    useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenConfirmation(event.currentTarget);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(null);
  };

  const openPopover = Boolean(openConfirmation);
  const id = openPopover ? 'confirm-popover' : undefined;

  return (
    <>
      <Tooltip title="Undo check-in">
        <IconButton
          color="primary"
          aria-label="undo"
          aria-describedby={id}
          onClick={handleClick}
        >
          <Replay />
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={openPopover}
        anchorEl={openConfirmation}
        onClose={handleCloseConfirmation}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Typography sx={{ p: 1, fontSize: '0.9rem' }}>
          Undo the check-in ?
        </Typography>
        <Box display={'flex'} gap={1} px={1} pb={1} justifyContent={'end'}>
          <Button
            onClick={handleCloseConfirmation}
            variant="contained"
            color="error"
            sx={{ fontSize: '0.7rem' }}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              handleConfirm(attendanceId);
              handleCloseConfirmation();
            }}
            variant="contained"
            color="primary"
            className="!text-white"
            sx={{ fontSize: '0.7rem' }}
          >
            Confirm
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default AttendanceDialog;
